<script setup>
import { useApi } from '@/plugins/api'
import { useV2Api } from '@/plugins/api/openapi'
import { useUtil } from '@/plugins/util'
import { useRouter } from '@/router'
import { useAuthStore } from '@/store/pinia/auth'
import AppIcon from '@/v2/components/ui/icon/AppIcon.vue'
import { useAlert } from '@/v2/composables/alert/alert'
import { onClickOutside, useStorage, useToggle } from '@vueuse/core'
import { nextTick, ref, watch } from 'vue'
import Teleport from 'vue2-teleport'
import AppButtonIcon from '../ui/button/AppButtonIcon.vue'
import NameAvatar from './partials/NameAvatar.vue'
import SearchInput from './partials/SearchInput.vue'

const alert = useAlert()
const api = useApi()
const v2api = useV2Api()
const authStore = useAuthStore()
const patients = ref([])
const searchText = ref('')
const loading = ref(false)
const util = useUtil()

async function fetchPatients() {
  loading.value = true
  try {
    const data = await v2api.patient.list({
      search: searchText.value,
      filter: {
        physicianId: {
          operation: 'equals',
          value: authStore.userId,
        },
      },
    })

    patients.value = data.items ?? []
  } catch (error) {
    alert.error({ error })
  } finally {
    loading.value = false
  }
}

const recentPatients = useStorage('kintsu-recent-patients', [])
function addRecentPatient(patient) {
  const patientAlreadyExists = recentPatients.value.some((p) => p.id === patient.id)
  if (patientAlreadyExists) {
    const patientIndex = recentPatients.value.findIndex((p) => p.id === patient.id)
    recentPatients.value.splice(patientIndex, 1)
  }
  recentPatients.value = [patient, ...recentPatients.value].slice(0, 5)
}

const router = useRouter()
async function selectPatient(patient) {
  addRecentPatient(patient)
  await nextTick()

  isOpen.value = false
  router.push({ name: 'patient-attendences', params: { id: patient.id } })
}

const modalContent = ref()
const [isOpen, toggle] = useToggle(false)
onClickOutside(modalContent, () => {
  isOpen.value = false
})

watch(searchText, fetchPatients, { immediate: true })
</script>

<template>
  <div>
    <template v-if="!isOpen">
      <SearchInput class="tw-w-full tw-bg-white max-sm:tw-hidden" @focus="isOpen = true" />
      <AppButtonIcon class="sm:tw-hidden" @click="isOpen = true">
        <AppIcon icon="magnify" />
      </AppButtonIcon>
    </template>

    <Teleport v-else to="#app">
      <div class="modal-active tw-fixed tw-z-20 tw-h-full tw-w-full tw-bg-blue-900/20 tw-p-4">
        <div
          ref="modalContent"
          class="tw-w-full tw-max-w-xl tw-space-y-4 tw-rounded-2xl tw-bg-white tw-p-4 md:tw-absolute md:tw-right-16"
        >
          <SearchInput v-model="searchText" :focused="isOpen" />

          <div class="tw-max-h-[380px] tw-space-y-2 tw-overflow-y-auto">
            <template v-if="recentPatients?.length && !searchText">
              <p class="tw-text-xs tw-leading-4 tw-text-body-2">Recente</p>

              <div
                v-for="(patient, index) in recentPatients"
                :key="`${patient.name}_${patient.id}`"
                class="tw-flex tw-h-11 tw-flex-row tw-items-center tw-justify-start tw-gap-3 tw-px-4 hover:tw-cursor-pointer hover:tw-rounded-lg hover:tw-bg-blue-gray-50"
                @click="() => selectPatient(patient)"
              >
                <AppIcon class="tw-fill-blue-gray-600" icon="schedule" />
                <div class="tw-flex tw-flex-col tw-items-start tw-justify-center">
                  <span class="tw-text-sm">{{ patient.name }}</span>
                  <span class="tw-text-2xs tw-text-body-2" v-if="patient.cpf">{{ util.maskCpf(patient.cpf) }}</span>
                </div>
                <div class="tw-grow" />
                <AppIcon
                  :size="20"
                  class="cursor-pointer tw-fill-blue-gray-600"
                  icon="close"
                  @click.stop="recentPatients.splice(index, 1)"
                />
              </div>
            </template>

            <div v-if="loading" class="tw-flex tw-h-60 tw-w-full tw-items-center tw-justify-center">
              <div class="tw-h-7 tw-w-7 tw-animate-spin tw-rounded-full tw-border-4 tw-border-b-transparent"></div>
            </div>

            <template v-else>
              <p class="tw-text-xs tw-leading-4 tw-text-body-2">Pacientes</p>

              <div
                v-for="patient in patients"
                :key="patient.id"
                class="tw-flex tw-h-11 tw-flex-row tw-items-center tw-justify-start tw-gap-3 tw-px-4 hover:tw-cursor-pointer hover:tw-rounded-lg hover:tw-bg-blue-gray-50"
                @click="() => selectPatient(patient)"
              >
                <NameAvatar :name="patient.name" />
                <div class="tw-flex tw-flex-col tw-items-start tw-justify-center">
                  <span class="font-size-14 tw-grow tw-text-body-1">{{ patient.name }}</span>
                  <span class="tw-grow tw-text-2xs tw-text-body-2" v-if="patient.cpf">{{
                    util.maskCpf(patient.cpf)
                  }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
<style scoped></style>
